import { ComponentProps, ComponentPropsWithoutRef, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { useNavigate } from "react-router-dom";
import { ButtonV2 } from "../ButtonV2";

export interface IContainerV2ButtonConfig {
  title?: string;
  icon?: ReactNode;
  onClick?: () => void;
  type?: ComponentProps<"button">["type"];
  disabled?: boolean;
  form?: string;
  /** Which side of the title the icon renders on. Defaults to "left" for the back button and "right" for the next button. */
  iconPosition?: "left" | "right";
  /** Extra classes merged onto the button (e.g. a fixed width for a screen-specific CTA). */
  className?: string;
}

interface IContainerV2Props extends ComponentPropsWithoutRef<"div"> {
  /** `false` hides the back button entirely. Omit for the default (shown, navigates back in history). Pass a config to customize title/icon/action/etc. */
  backButton?: false | IContainerV2ButtonConfig;
  /** Omit to hide. Pass a config to show it — `title` is the only field you'll usually need. */
  nextButton?: IContainerV2ButtonConfig;
}

export const ContainerV2 = ({
  className,
  children,
  backButton,
  nextButton,
  ...rest
}: IContainerV2Props) => {
  const navigate = useNavigate();

  const showBackButton = backButton !== false;
  const backButtonConfig = backButton || {};
  const showBothButtons = showBackButton && !!nextButton;

  return (
    <div
      {...rest}
      className={twMerge("py-5 w-full max-w-7xl mx-auto md:py-12", className)}
    >
      {children}

      <div
        className={twMerge(
          "mt-10 flex items-center justify-between gap-8 flex-col-reverse md:flex-row",
          !showBothButtons && "justify-center",
        )}
      >
        {showBackButton && (
          <ButtonV2
            className={twMerge("pl-0", backButtonConfig.className)}
            variant="text"
            type={backButtonConfig.type ?? "button"}
            disabled={backButtonConfig.disabled}
            form={backButtonConfig.form}
            onClick={backButtonConfig.onClick ?? (() => navigate(-1))}
          >
            {(backButtonConfig.iconPosition ?? "left") === "left" &&
              (backButtonConfig.icon ?? (
                <img
                  src="/assets/svgs/icon-arrow-back-red.svg"
                  alt="icon voltar"
                />
              ))}
            {backButtonConfig.title ?? "Voltar"}
            {backButtonConfig.iconPosition === "right" &&
              (backButtonConfig.icon ?? (
                <img
                  src="/assets/svgs/icon-arrow-back-red.svg"
                  alt="icon voltar"
                />
              ))}
          </ButtonV2>
        )}

        {nextButton && (
          <ButtonV2
            className={nextButton.className}
            type={nextButton.type ?? (nextButton.form ? "submit" : "button")}
            disabled={nextButton.disabled}
            form={nextButton.form}
            onClick={nextButton.onClick}
          >
            {nextButton.iconPosition === "left" &&
              (nextButton.icon ?? (
                <img
                  src="/assets/svgs/icon-arrow-forward.svg"
                  alt="icon avançar"
                />
              ))}
            {nextButton.title}
            {(nextButton.iconPosition ?? "right") === "right" &&
              (nextButton.icon ?? (
                <img
                  src="/assets/svgs/icon-arrow-forward.svg"
                  alt="icon avançar"
                />
              ))}
          </ButtonV2>
        )}
      </div>
    </div>
  );
};
